import React from 'react'
import { createContext } from 'lib/createContext'
import { Loading, Message } from '@workwhile/ui'
import { parseErrorMessage } from 'api'
import { useUserPayments } from 'queries/payments/useUserPayments'
import { useUserExternalBankAccounts } from 'queries/payments/useUserPaymentMethods'
import { useUserOutstandingBalance } from 'queries/payments/useUserOutstandingBalance'
import { usePayoutToAchMutation } from 'queries/payments/usePayoutToAchMutation'
import { useCancelBonusMutation } from 'queries/payments/useCancelBonusMutation'
import { usePaginationQueryState } from 'lib/nuqs/usePaginationQueryState'
import { usePaymentFiltersQueryState } from '../hooks/usePaymentFiltersQueryState'
import { useTimeRangeQueryState } from '../hooks/useTimeRangeQueryState'
import { useSelectedColumnsQueryState } from '../hooks/useSelectedColumnsQueryState'
import { SelectableColumns } from '../components/ColumnsFilter'
import {
  CancelBonusRequest,
  CancelBonusResponse,
  GetUserPaymentsResponse,
  HighnoteExternalBankAccountResponse,
  PayoutToAchRequest,
  PayoutToAchResponse,
  UserPayment,
  UserPaymentAdminAction,
  UserPaymentEventType,
  UserPaymentEventSubType,
  UserPaymentProvider,
  UserPaymentStatus,
} from 'api/payments'

type PayoutToAchModalIdentity = {
  userId: number
  userName?: string | null
  paymentId?: number
}

type PayoutToAchOrchestration = {
  isLoading: boolean
  statusMessage: string | null
  canPayout: boolean
  fullBalance: number
  selectedPayment: UserPayment | undefined
  activeBankAccount: HighnoteExternalBankAccountResponse | undefined
  mutationState: {
    isPending: boolean
    errorMessage: string | undefined
  }
}

type PayoutToAchState = PayoutToAchModalIdentity & PayoutToAchOrchestration

type CancelBonusState = {
  payment: UserPayment
  mutationState: {
    isPending: boolean
    errorMessage: string | undefined
  }
}

type UserPaymentsContextState = {
  state: {
    data: GetUserPaymentsResponse
    dataUpdatedAt: number
    isFetching: boolean
    isError: boolean
    error: string
    pagination: {
      pageIndex: number
      pageSize: number
    }
    filters: {
      paymentId: number | null
      userId: number | null
      status: string[] | null
      eventType: string[] | null
      eventSubType: string[] | null
      type: string[] | null
      paymentProvider: string[] | null
      shiftId: number | null
      companyId: number | null
    }
    timeRangeFilters: {
      startDate?: Date
      endDate?: Date
    }
    selectedColumnsFilters: SelectableColumns[]
    payoutToAch: PayoutToAchState | null
    cancelBonus: CancelBonusState | null
  }
  actions: {
    refetch: () => void
    setPagination: ReturnType<typeof usePaginationQueryState>[1]
    setFilters: ReturnType<typeof usePaymentFiltersQueryState>[1]
    setTimeRangeFilters: ReturnType<typeof useTimeRangeQueryState>[1]
    setSelectedColumnsFilters: ReturnType<
      typeof useSelectedColumnsQueryState
    >[1]
    openPayoutToAch: (state: PayoutToAchModalIdentity) => void
    closePayoutToAch: () => void
    submitPayoutToAch: (
      request: Omit<PayoutToAchRequest, 'userId'>,
      options?: {
        onSuccess?: (data: PayoutToAchResponse) => void
      }
    ) => void
    openCancelBonus: (payment: UserPayment) => void
    closeCancelBonus: () => void
    submitCancelBonus: (
      request: Omit<CancelBonusRequest, 'userPaymentId'>,
      options?: {
        onSuccess?: (data: CancelBonusResponse) => void
      }
    ) => void
  }
}

const [Context, useUserPaymentsContextValue] =
  createContext<UserPaymentsContextState>({
    name: 'UserPaymentsContext',
  })

const getPayoutToAchStatusMessage = ({
  isLoading,
  paymentId,
  bankAccountsError,
  outstandingBalanceError,
  paymentsError,
  activeBankAccount,
  fullBalance,
  selectedPayment,
}: {
  isLoading: boolean
  paymentId?: number
  bankAccountsError: unknown
  outstandingBalanceError: unknown
  paymentsError: unknown
  activeBankAccount: HighnoteExternalBankAccountResponse | undefined
  fullBalance: number
  selectedPayment: UserPayment | undefined
}): string | null => {
  if (isLoading) {
    return null
  }
  if (bankAccountsError) {
    return (
      parseErrorMessage(bankAccountsError) ||
      'Could not load the worker bank account.'
    )
  }
  if (outstandingBalanceError) {
    return (
      parseErrorMessage(outstandingBalanceError) ||
      'Could not load the worker Highnote balance.'
    )
  }
  if (paymentId != null && paymentsError) {
    return (
      parseErrorMessage(paymentsError) || 'Could not load the selected payment.'
    )
  }
  if (!activeBankAccount) {
    return 'Worker has no active bank account with Highnote.'
  }
  if (fullBalance <= 0) {
    return 'Worker has no pending Highnote balance eligible for ACH payout.'
  }
  if (paymentId != null && !selectedPayment) {
    return `Payment ${paymentId} is not eligible for ACH payout.`
  }
  return null
}

export const UserPaymentsProvider = ({
  children,
  initialUserId,
  initialShiftId,
  initialSelectedColumns,
}: {
  children: React.ReactNode
  initialUserId?: number
  initialShiftId?: number
  initialSelectedColumns?: SelectableColumns[]
}) => {
  const [pagination, setPagination] = usePaginationQueryState({
    pageIndexParser: 'integer',
  })
  const [filters, setFilters] = usePaymentFiltersQueryState()
  const [timeRangeFilters, setTimeRangeFilters] = useTimeRangeQueryState()
  const [selectedColumnsFilters, setSelectedColumnsFilters] =
    useSelectedColumnsQueryState()
  const [payoutToAchModal, setPayoutToAchModal] =
    React.useState<PayoutToAchModalIdentity | null>(null)
  const [cancelBonusPayment, setCancelBonusPayment] =
    React.useState<UserPayment | null>(null)

  // Override selected columns if initialSelectedColumns is provided and columns are at default value
  const effectiveSelectedColumns = React.useMemo(() => {
    if (
      initialSelectedColumns &&
      JSON.stringify(selectedColumnsFilters) ===
        JSON.stringify(['user', 'provider', 'originated-at', 'comment'])
    ) {
      return initialSelectedColumns
    }
    return selectedColumnsFilters
  }, [selectedColumnsFilters, initialSelectedColumns])

  // Initialize selected columns once on mount if initialSelectedColumns is provided
  React.useEffect(() => {
    if (initialSelectedColumns) {
      setSelectedColumnsFilters(initialSelectedColumns)
    }
    // Only run once on mount
  }, [])

  // Override userId and shiftId filters if provided
  const effectiveFilters = React.useMemo(() => {
    let result = filters
    if (initialUserId !== undefined) {
      result = {
        ...result,
        userId: initialUserId,
      }
    }
    if (initialShiftId !== undefined) {
      result = {
        ...result,
        shiftId: initialShiftId,
      }
    }
    return result
  }, [filters, initialUserId, initialShiftId])

  const {
    data,
    isPending: isPaymentsLoading,
    isError: isPaymentsError,
    error: paymentsError,
    dataUpdatedAt,
    refetch,
    isFetching,
  } = useUserPayments({
    userId: effectiveFilters.userId ?? undefined,
    status:
      (effectiveFilters.status?.filter(Boolean) as UserPaymentStatus[]) ??
      undefined,
    eventType:
      (effectiveFilters.eventType?.filter(Boolean) as UserPaymentEventType[]) ??
      undefined,
    eventSubType:
      (effectiveFilters.eventSubType?.filter(
        Boolean
      ) as UserPaymentEventSubType[]) ?? undefined,
    type: (effectiveFilters.type?.filter(Boolean) as string[]) ?? undefined,
    paymentProvider:
      (effectiveFilters.paymentProvider?.filter(
        Boolean
      ) as UserPaymentProvider[]) ?? undefined,
    paymentId: effectiveFilters.paymentId ?? undefined,
    shiftId: effectiveFilters.shiftId ?? undefined,
    companyId: effectiveFilters.companyId ?? undefined,
    page: pagination.pageIndex,
    pageSize: pagination.pageSize,
    startDate: timeRangeFilters.startDate,
    endDate: timeRangeFilters.endDate,
  })

  const isPayoutModalOpen = payoutToAchModal != null
  const payoutUserId = payoutToAchModal?.userId ?? 0
  const payoutPaymentId = payoutToAchModal?.paymentId

  const payoutMutation = usePayoutToAchMutation()
  const cancelBonusMutation = useCancelBonusMutation()
  const selectedPaymentQuery = useUserPayments(
    {
      userId: payoutUserId,
      paymentId: payoutPaymentId,
      pageSize: 1,
    },
    { enabled: isPayoutModalOpen && payoutPaymentId != null }
  )
  const bankAccountsQuery = useUserExternalBankAccounts(
    { userId: payoutUserId },
    { enabled: isPayoutModalOpen }
  )
  const outstandingBalanceQuery = useUserOutstandingBalance(
    { userId: payoutUserId },
    { enabled: isPayoutModalOpen }
  )

  if (isPaymentsLoading) {
    return <Loading />
  }

  if (isPaymentsError && !data) {
    return (
      <Message variant="error" m={4} borderRadius="small">
        Sorry, something went wrong. Please reach out to eng support with the
        URL of the page if this persists.
      </Message>
    )
  }

  const fullBalance =
    (outstandingBalanceQuery.data?.earningsWithHighnote ?? 0) +
    (outstandingBalanceQuery.data?.completedHighnotePayouts ?? 0) +
    (outstandingBalanceQuery.data?.pendingHighnotePayouts ?? 0)
  const selectedPayment = selectedPaymentQuery.data?.items.find((payment) =>
    payment.availableActions.includes(UserPaymentAdminAction.PAYOUT_TO_ACH)
  )
  const activeBankAccount = bankAccountsQuery.data?.externalBankAccounts.find(
    (account) => !account.archivedAt
  )
  const isPayoutLoading =
    bankAccountsQuery.isPending ||
    outstandingBalanceQuery.isPending ||
    (payoutPaymentId != null && selectedPaymentQuery.isPending)
  const payoutStatusMessage = getPayoutToAchStatusMessage({
    isLoading: isPayoutLoading,
    paymentId: payoutPaymentId,
    bankAccountsError: bankAccountsQuery.isError
      ? bankAccountsQuery.error
      : null,
    outstandingBalanceError: outstandingBalanceQuery.isError
      ? outstandingBalanceQuery.error
      : null,
    paymentsError: selectedPaymentQuery.isError
      ? selectedPaymentQuery.error
      : null,
    activeBankAccount,
    fullBalance,
    selectedPayment,
  })
  const canPayout = !isPayoutLoading && payoutStatusMessage == null

  const payoutToAch: PayoutToAchState | null = payoutToAchModal
    ? {
        ...payoutToAchModal,
        isLoading: isPayoutLoading,
        statusMessage: payoutStatusMessage,
        canPayout,
        fullBalance,
        selectedPayment,
        activeBankAccount,
        mutationState: {
          isPending: payoutMutation.isPending,
          errorMessage: payoutMutation.isError
            ? parseErrorMessage(payoutMutation.error) ||
              'ACH payout failed. Try again or contact engineering.'
            : undefined,
        },
      }
    : null

  const cancelBonus: CancelBonusState | null = cancelBonusPayment
    ? {
        payment: cancelBonusPayment,
        mutationState: {
          isPending: cancelBonusMutation.isPending,
          errorMessage: cancelBonusMutation.isError
            ? parseErrorMessage(cancelBonusMutation.error) ||
              'Bonus cancel failed. Try again or contact engineering.'
            : undefined,
        },
      }
    : null

  const value: UserPaymentsContextState = {
    state: {
      data: data ?? {
        items: [],
        totalRows: 0,
        totalPages: 0,
        currentPage: 0,
        pageSize: pagination.pageSize,
      },
      dataUpdatedAt,
      isFetching,
      isError: isPaymentsError,
      error: parseErrorMessage(paymentsError),
      pagination,
      filters: effectiveFilters,
      timeRangeFilters,
      selectedColumnsFilters: effectiveSelectedColumns,
      payoutToAch,
      cancelBonus,
    },
    actions: {
      refetch,
      setPagination,
      setFilters,
      setTimeRangeFilters,
      setSelectedColumnsFilters,
      openPayoutToAch: setPayoutToAchModal,
      closePayoutToAch: () => {
        payoutMutation.reset()
        setPayoutToAchModal(null)
      },
      submitPayoutToAch: (request, options) => {
        if (!payoutToAchModal) {
          return
        }
        payoutMutation.mutate(
          { ...request, userId: payoutToAchModal.userId },
          { onSuccess: options?.onSuccess }
        )
      },
      openCancelBonus: setCancelBonusPayment,
      closeCancelBonus: () => {
        cancelBonusMutation.reset()
        setCancelBonusPayment(null)
      },
      submitCancelBonus: (request, options) => {
        if (!cancelBonusPayment) {
          return
        }
        cancelBonusMutation.mutate(
          { ...request, userPaymentId: cancelBonusPayment.id },
          { onSuccess: options?.onSuccess }
        )
      },
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useUserPaymentsContextValue }
